import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { models } from 'powerbi-client';
import {
  PowerBiStoredSlicerState,
  PowerBiFilterStateSnapshot,
  buildPowerBiFilterStateStorageKey,
  createPowerBiFilterStateSnapshot,
  getPowerBiSlicerTargetKey,
  loadPowerBiFilterStateSnapshot,
  savePowerBiFilterStateSnapshot,
  clearPowerBiFilterStateSnapshot,
  upsertPowerBiPageState,
} from './powerBiFilterState';

export interface PowerBiFilterStateBridgeVisual {
  name?: string;
  title?: string;
  layout?: {
    displayState?: {
      mode?: models.VisualContainerDisplayMode;
    };
  };
  getSlicerState?: () => Promise<unknown>;
  setSlicerState?(state: unknown): Promise<unknown>;
}

export interface PowerBiFilterStateBridgePage {
  name?: string;
  getFilters?: () => Promise<unknown[]>;
  updateFilters?(operation: models.FiltersOperations, filters: models.IFilter[]): Promise<unknown>;
  getSlicers?: () => Promise<PowerBiFilterStateBridgeVisual[]>;
}

export interface PowerBiFilterStateBridgeReport {
  getFilters?: () => Promise<unknown[]>;
  updateFilters?(operation: models.FiltersOperations, filters: models.IFilter[]): Promise<unknown>;
  getActivePage?: () => Promise<PowerBiFilterStateBridgePage>;
}

interface UsePowerBiFilterStateBridgeParams {
  report: PowerBiFilterStateBridgeReport | null;
  userKey?: string;
  projectId?: string;
  reportId?: string;
  pageName?: string;
  enabled?: boolean;
  storage?: Storage;
}

interface UsePowerBiFilterStateBridgeResult {
  applyState: (
    reportOverride?: PowerBiFilterStateBridgeReport | null,
    pageOverride?: PowerBiFilterStateBridgePage | null
  ) => Promise<void>;
  captureState: (
    reportOverride?: PowerBiFilterStateBridgeReport | null,
    shouldPersist?: () => boolean
  ) => Promise<void>;
  clearState: () => void;
  hasUserChangedDate: boolean;
}

interface PowerBiCurrentSlicerState {
  visual: PowerBiFilterStateBridgeVisual;
  storedSlicer: PowerBiStoredSlicerState;
}

export const usePowerBiFilterStateBridge = ({
  report,
  userKey,
  projectId,
  reportId,
  pageName,
  enabled = true,
  storage = getBrowserLocalStorage(),
}: UsePowerBiFilterStateBridgeParams): UsePowerBiFilterStateBridgeResult => {
  const storageKey = useMemo(() => {
    if (!enabled || !userKey || !projectId || !reportId) {
      return null;
    }

    return buildPowerBiFilterStateStorageKey({ userKey, projectId, reportId });
  }, [enabled, projectId, reportId, userKey]);

  const [hasUserChangedDate, setHasUserChangedDate] = useState(() =>
    Boolean(
      storage && storageKey && loadPowerBiFilterStateSnapshot(storage, storageKey)?.userChangedDate
    )
  );
  const latestSnapshotRef = useRef<PowerBiFilterStateSnapshot | null>(
    storage && storageKey ? loadPowerBiFilterStateSnapshot(storage, storageKey) : null
  );

  useEffect(() => {
    const snapshot =
      storage && storageKey ? loadPowerBiFilterStateSnapshot(storage, storageKey) : null;
    latestSnapshotRef.current = snapshot;
    setHasUserChangedDate(Boolean(snapshot?.userChangedDate));
  }, [storage, storageKey]);

  const captureState = useCallback(
    async (
      reportOverride?: PowerBiFilterStateBridgeReport | null,
      shouldPersist: () => boolean = () => true
    ) => {
      const activeReport = reportOverride ?? report;

      if (!enabled || !storage || !storageKey || !activeReport || !pageName) {
        return;
      }

      try {
        const storedSnapshot =
          latestSnapshotRef.current ?? loadPowerBiFilterStateSnapshot(storage, storageKey);
        const existingSnapshot =
          storedSnapshot ?? createPowerBiFilterStateSnapshot({ reportFilters: [] });
        const [reportFilters, activePage] = await Promise.all([
          readFilters(activeReport),
          getActivePage(activeReport),
        ]);

        if (!activePage) {
          return;
        }

        const activePageName = activePage.name || pageName;
        const knownSlicers = [
          ...existingSnapshot.sharedDateSlicers,
          ...(existingSnapshot.pages[activePageName]?.slicers ?? []),
        ];
        const [pageFilters, slicers] = await Promise.all([
          readFilters(activePage),
          readSlicerStates(activePage, knownSlicers),
        ]);
        const dateReportFilters = filterPowerBiDateFilters(reportFilters);
        const datePageFilters = filterPowerBiDateFilters(pageFilters);
        const dateSlicers = filterPowerBiDateSlicers(slicers);
        const sharedDateSlicers = mergeStoredSlicerStates(
          existingSnapshot.sharedDateSlicers,
          dateSlicers
        );
        const userChangedDate =
          existingSnapshot.userChangedDate ||
          Boolean(
            storedSnapshot &&
              hasExistingDateStateChanged(
                existingSnapshot,
                activePageName,
                dateReportFilters,
                datePageFilters,
                dateSlicers
              )
          );
        const nextSnapshot = upsertPowerBiPageState(
          {
            ...existingSnapshot,
            reportFilters: dateReportFilters,
            sharedDateSlicers,
            userChangedDate,
          },
          activePageName,
          {
            pageFilters: datePageFilters,
            slicers: dateSlicers,
          }
        );

        if (!shouldPersist()) {
          return;
        }

        savePowerBiFilterStateSnapshot(storage, storageKey, nextSnapshot);
        latestSnapshotRef.current = nextSnapshot;
        setHasUserChangedDate(userChangedDate);
      } catch (error) {
        console.warn('Failed to capture Power BI filter state:', error);
      }
    },
    [enabled, pageName, report, storage, storageKey]
  );

  const applyState = useCallback(
    async (
      reportOverride?: PowerBiFilterStateBridgeReport | null,
      pageOverride?: PowerBiFilterStateBridgePage | null
    ) => {
      const activeReport = reportOverride ?? report;

      if (!enabled || !storage || !storageKey || !activeReport || !pageName) {
        return;
      }

      const snapshot =
        latestSnapshotRef.current ?? loadPowerBiFilterStateSnapshot(storage, storageKey);

      if (!snapshot) {
        setHasUserChangedDate(false);
        return;
      }

      setHasUserChangedDate(snapshot.userChangedDate);

      const storedReportFilters = filterPowerBiDateFilters(snapshot.reportFilters);
      await applyReportFilters(activeReport, storedReportFilters);

      const activePage = pageOverride ?? (await getActivePage(activeReport));

      if (!activePage) {
        return;
      }

      const activePageName = activePage.name || pageName;
      const storedPage = snapshot.pages[activePageName] ?? snapshot.pages[pageName];
      const sharedDateSlicers = filterPowerBiDateSlicers(snapshot.sharedDateSlicers);

      if (!storedPage && !sharedDateSlicers.length) {
        return;
      }

      const storedPageFilters = filterPowerBiDateFilters(storedPage?.pageFilters ?? []);
      const storedSlicers = sharedDateSlicers.length
        ? sharedDateSlicers
        : storedPage?.slicers ?? [];
      await applyPageFilters(activePage, storedPageFilters);
      await applySlicerStates(activePage, storedSlicers);
    },
    [enabled, pageName, report, storage, storageKey]
  );

  const clearState = useCallback(() => {
    if (!storage || !storageKey) {
      return;
    }

    clearPowerBiFilterStateSnapshot(storage, storageKey);
    latestSnapshotRef.current = null;
    setHasUserChangedDate(false);
  }, [storage, storageKey]);

  return {
    applyState,
    captureState,
    clearState,
    hasUserChangedDate,
  };
};

const hasExistingDateStateChanged = (
  existingSnapshot: PowerBiFilterStateSnapshot,
  activePageName: string,
  reportFilters: unknown[],
  pageFilters: unknown[],
  slicers: PowerBiStoredSlicerState[]
) => {
  if (!hasSameSerializableValue(existingSnapshot.reportFilters, reportFilters)) {
    return true;
  }

  const existingPage = existingSnapshot.pages[activePageName];
  if (existingPage && !hasSameSerializableValue(existingPage.pageFilters ?? [], pageFilters)) {
    return true;
  }

  return slicers.some((slicer) => {
    const existingSlicer = existingSnapshot.sharedDateSlicers.find(
      (candidate) =>
        candidate.targetKey === slicer.targetKey || candidate.visualName === slicer.visualName
    );

    if (!existingSlicer) {
      return Boolean(existingPage);
    }

    return !hasSameSerializableValue(existingSlicer.state, slicer.state);
  });
};

const hasSameSerializableValue = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

const readFilters = async (owner: {
  getFilters?: () => Promise<unknown[]>;
}): Promise<unknown[]> => {
  try {
    return (await owner.getFilters?.()) ?? [];
  } catch (error) {
    console.warn('Failed to read Power BI filters:', error);
    return [];
  }
};

const readSlicerStates = async (
  page: PowerBiFilterStateBridgePage,
  knownSlicers: PowerBiStoredSlicerState[] = []
): Promise<PowerBiStoredSlicerState[]> => {
  if (!page.getSlicers) {
    return [];
  }

  const slicers = await page.getSlicers().catch((error: unknown) => {
    console.warn('Failed to read Power BI slicers:', error);
    return [];
  });

  if (!slicers?.length) {
    return [];
  }

  const storedSlicers: PowerBiStoredSlicerState[] = [];

  for (const slicer of slicers.filter(isVisiblePowerBiSlicer)) {
    if (!slicer.getSlicerState || !slicer.name) {
      continue;
    }

    try {
      const state = await slicer.getSlicerState();
      const serializableState = cloneSerializableState(state);
      const targetKeyFromState = getPowerBiSlicerTargetKey(serializableState);
      const knownSlicer = findKnownSlicer(
        knownSlicers,
        slicer.name,
        slicer.title,
        targetKeyFromState
      );
      const targetKey = targetKeyFromState || knownSlicer?.targetKey || null;

      if (!serializableState || !targetKey) {
        continue;
      }

      storedSlicers.push({
        visualName: slicer.name,
        title: slicer.title,
        targetKey,
        state: serializableState,
      });
    } catch (error) {
      console.warn('Failed to read Power BI slicer state:', error);
    }
  }

  return storedSlicers;
};

const applyReportFilters = async (
  report: PowerBiFilterStateBridgeReport,
  reportFilters: unknown[]
): Promise<void> => {
  if (!reportFilters.length || !report.updateFilters) {
    return;
  }

  try {
    const currentFilters = await readFilters(report);
    const nextFilters = mergeStoredDateFilters(currentFilters, reportFilters);
    await report.updateFilters(models.FiltersOperations.Replace, nextFilters as models.IFilter[]);
  } catch (error) {
    console.warn('Failed to apply Power BI report filters:', error);
  }
};

const applyPageFilters = async (
  page: PowerBiFilterStateBridgePage,
  pageFilters: unknown[]
): Promise<void> => {
  if (!pageFilters.length || !page.updateFilters) {
    return;
  }

  try {
    const currentFilters = await readFilters(page);
    const nextFilters = mergeStoredDateFilters(currentFilters, pageFilters);
    await page.updateFilters(models.FiltersOperations.Replace, nextFilters as models.IFilter[]);
  } catch (error) {
    console.warn('Failed to apply Power BI page filters:', error);
  }
};

const applySlicerStates = async (
  page: PowerBiFilterStateBridgePage,
  storedSlicers: PowerBiStoredSlicerState[]
): Promise<void> => {
  const storedDateSlicers = filterPowerBiDateSlicers(storedSlicers);

  if (!storedDateSlicers.length || !page.getSlicers) {
    return;
  }

  const currentSlicers = (
    await page.getSlicers().catch((error: unknown) => {
      console.warn('Failed to read Power BI slicers while applying state:', error);
      return [];
    })
  ).filter(isVisiblePowerBiSlicer);
  const currentSlicerStates = await readCurrentSlicerStates(currentSlicers);

  for (const storedSlicer of storedDateSlicers) {
    const matchingSlicer = findMatchingSlicer(
      currentSlicers,
      currentSlicerStates,
      storedSlicer,
      storedDateSlicers
    );

    if (!matchingSlicer?.setSlicerState) {
      continue;
    }

    try {
      await matchingSlicer.setSlicerState(storedSlicer.state);
    } catch (error) {
      console.warn('Failed to apply Power BI slicer state:', error);
    }
  }
};

const readCurrentSlicerStates = async (
  currentSlicers: PowerBiFilterStateBridgeVisual[]
): Promise<PowerBiCurrentSlicerState[]> => {
  const states: PowerBiCurrentSlicerState[] = [];

  for (const slicer of currentSlicers) {
    if (!slicer.getSlicerState || !slicer.name) {
      continue;
    }

    try {
      const state = await slicer.getSlicerState();
      const serializableState = cloneSerializableState(state);
      const targetKey = getPowerBiSlicerTargetKey(serializableState);

      if (!serializableState || !targetKey) {
        continue;
      }

      states.push({
        storedSlicer: {
          state: serializableState,
          targetKey,
          title: slicer.title,
          visualName: slicer.name,
        },
        visual: slicer,
      });
    } catch (error) {
      console.warn('Failed to inspect Power BI slicer state while matching:', error);
    }
  }

  return states;
};

const findMatchingSlicer = (
  currentSlicers: PowerBiFilterStateBridgeVisual[],
  currentSlicerStates: PowerBiCurrentSlicerState[],
  storedSlicer: PowerBiStoredSlicerState,
  storedSlicers: PowerBiStoredSlicerState[]
) => {
  const nameMatch = currentSlicers.find((slicer) => slicer.name === storedSlicer.visualName);

  if (nameMatch) {
    return nameMatch;
  }

  const targetMatch = currentSlicerStates.find(
    ({ storedSlicer: currentSlicer }) => currentSlicer.targetKey === storedSlicer.targetKey
  )?.visual;

  if (targetMatch) {
    return targetMatch;
  }

  const titleMatches = currentSlicers.filter(
    (slicer) => !!storedSlicer.title && slicer.title === storedSlicer.title
  );
  const storedTitleTargets = new Set(
    storedSlicers
      .filter((candidate) => !!storedSlicer.title && candidate.title === storedSlicer.title)
      .map((candidate) => candidate.targetKey)
  );

  return titleMatches.length === 1 && storedTitleTargets.size === 1 ? titleMatches[0] : null;
};

const filterPowerBiDateFilters = (filters: unknown[]) => filters.filter(isPowerBiDateFilter);

const mergeStoredDateFilters = (currentFilters: unknown[], storedDateFilters: unknown[]) => [
  ...currentFilters.filter((filter) => !isPowerBiDateFilter(filter)),
  ...filterPowerBiDateFilters(storedDateFilters),
];

const filterPowerBiDateSlicers = (slicers: PowerBiStoredSlicerState[]) =>
  slicers.filter(isPowerBiDateSlicer);

const mergeStoredSlicerStates = (
  existingSlicers: PowerBiStoredSlicerState[],
  capturedSlicers: PowerBiStoredSlicerState[]
) =>
  capturedSlicers.reduce<PowerBiStoredSlicerState[]>(
    (mergedSlicers, capturedSlicer) => {
      const unrelatedSlicers = mergedSlicers.filter(
        (existingSlicer) =>
          existingSlicer.targetKey !== capturedSlicer.targetKey &&
          existingSlicer.visualName !== capturedSlicer.visualName
      );

      return [...unrelatedSlicers, capturedSlicer];
    },
    [...existingSlicers]
  );

const isVisiblePowerBiSlicer = (slicer: PowerBiFilterStateBridgeVisual) =>
  slicer.layout?.displayState?.mode !== models.VisualContainerDisplayMode.Hidden;

const findKnownSlicer = (
  knownSlicers: PowerBiStoredSlicerState[],
  visualName: string,
  title: string | undefined,
  targetKey: string
) => {
  const nameMatch = knownSlicers.find((candidate) => candidate.visualName === visualName);

  if (nameMatch) {
    return nameMatch;
  }

  if (targetKey) {
    const targetMatch = knownSlicers.find((candidate) => candidate.targetKey === targetKey);

    if (targetMatch) {
      return targetMatch;
    }
  }

  if (!title) {
    return null;
  }

  const titleMatchesByTarget = new Map<string, PowerBiStoredSlicerState>();
  knownSlicers
    .filter((candidate) => candidate.title === title)
    .forEach((candidate) => titleMatchesByTarget.set(candidate.targetKey, candidate));

  return titleMatchesByTarget.size === 1 ? [...titleMatchesByTarget.values()][0] : null;
};

const isPowerBiDateSlicer = (slicer: PowerBiStoredSlicerState) =>
  isPowerBiDateTargetKey(slicer.targetKey) ||
  getPowerBiSlicerFilters(slicer.state).some(isPowerBiDateFilter);

const isPowerBiDateFilter = (filter: unknown) => {
  const filterRecord = asRecord(filter);

  if (!filterRecord) {
    return false;
  }

  return isPowerBiDateTargetKey(
    getPowerBiFilterTargetKey(filterRecord.target ?? filterRecord.targets)
  );
};

const isPowerBiDateTargetKey = (targetKey: string) => {
  const normalizedTargetKey = targetKey.toLowerCase();

  return (
    normalizedTargetKey === 'date.calendar date' ||
    normalizedTargetKey === 'vw_dates.date' ||
    normalizedTargetKey.split(/[.\s_]+/).some((part) => part === 'date' || part === 'dates')
  );
};

const getActivePage = async (report: PowerBiFilterStateBridgeReport) => {
  try {
    return await report.getActivePage?.();
  } catch {
    return null;
  }
};

const cloneSerializableState = (state: unknown): unknown => {
  try {
    const serialized = JSON.stringify(state);

    if (!serialized) {
      return null;
    }

    return JSON.parse(serialized) as unknown;
  } catch {
    return null;
  }
};

type UnknownRecord = Record<string, unknown>;

const getPowerBiSlicerFilters = (slicerState: unknown): unknown[] => {
  const stateRecord = asRecord(slicerState);

  if (!stateRecord || !Array.isArray(stateRecord.filters)) {
    return [];
  }

  return stateRecord.filters;
};

const getPowerBiFilterTargetKey = (target: unknown): string => {
  if (Array.isArray(target)) {
    return target.map(getPowerBiFilterTargetKey).filter(Boolean).join(' + ');
  }

  const targetRecord = asRecord(target);

  if (!targetRecord) {
    return '';
  }

  const table = toStringValue(targetRecord.table);
  const column = toStringValue(targetRecord.column);
  const hierarchy = toStringValue(targetRecord.hierarchy);
  const hierarchyLevel = toStringValue(targetRecord.hierarchyLevel);
  const measure = toStringValue(targetRecord.measure);

  if (table && column) {
    return `${table}.${column}`;
  }

  if (table && hierarchy && hierarchyLevel) {
    return `${table}.${hierarchy}.${hierarchyLevel}`;
  }

  if (table && measure) {
    return `${table}.${measure}`;
  }

  return '';
};

const toStringValue = (value: unknown): string => (typeof value === 'string' ? value : '');

const asRecord = (value: unknown): UnknownRecord | null => (isRecord(value) ? value : null);

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getBrowserLocalStorage = () =>
  typeof window === 'undefined' ? undefined : window.localStorage;
